import type { VehicleInput } from '@polyloop/sim';

/**
 * Keyboard mapping (WASD + alternates).
 *
 *   W / ↑       throttle forward
 *   S / ↓       reverse / brake (S = reverse when stopped, brake while moving)
 *   A / ←       steer left
 *   D / →       steer right
 *   Space       boost
 *   Shift       drift
 *   B           brake (explicit)
 *   R           reset to spawn
 *   C           look back (camera invert, post-v1)
 */
export class KeyboardInput {
  private keys = new Set<string>();

  attach(): void {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  detach(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.keys.clear();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    this.keys.add(e.code);
    // Prevent page scroll on space + arrows.
    if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };
  private onBlur = (): void => {
    this.keys.clear();
  };

  read(into: VehicleInput): VehicleInput {
    const fwd = this.keys.has('KeyW') || this.keys.has('ArrowUp') ? 1 : 0;
    const rev = this.keys.has('KeyS') || this.keys.has('ArrowDown') ? 1 : 0;
    const left = this.keys.has('KeyA') || this.keys.has('ArrowLeft') ? 1 : 0;
    const right = this.keys.has('KeyD') || this.keys.has('ArrowRight') ? 1 : 0;

    into.throttle = fwd - rev;
    into.steer = right - left;
    into.drift = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    into.boost = this.keys.has('Space');
    into.brake = this.keys.has('KeyB');
    into.reset = this.keys.has('KeyR');
    into.lookBack = this.keys.has('KeyC');
    return into;
  }
}
